import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FaFlask, FaSync } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { useEnv } from "../OrgUsageAnalytics/useEnv";
import { fetchFdeRoster, fetchPilots } from "./api";
import { ClientOverviewProvider } from "./ClientOverviewContext";
import { PLAN_PILOT_ID } from "./costSelectors";
import { FdeView } from "./components/FdeView";
import { PilotCostCalculator } from "./components/PilotCostCalculator";
import { RoiCalculator } from "./components/RoiCalculator";
import { EMPTY_ROI_STATE } from "./roiSelectors";
import { SalesView } from "./components/SalesView";
import { formatNumber } from "./selectors";
import "./Pilots.css";

const TABS = [
  { key: "pilots", label: "Pilots" },
  { key: "fde", label: "FDE view" },
  { key: "cost", label: "Pilot cost" },
  { key: "roi", label: "Client ROI" },
];

const FDE_SELF_STORAGE_KEY = "pilots-fde-self";
const OWNER_SELF_STORAGE_KEY = "pilots-owner-self";

function readStored(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key, value) {
  try {
    if (value) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Private mode or blocked storage: the choice just is not remembered.
  }
}

export default function PilotsPage({ onReady }) {
  const [env, setEnv] = useEnv();
  const history = useHistory();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [costState, setCostState] = useState({ pilotId: PLAN_PILOT_ID, edits: {} });
  const [roiState, setRoiState] = useState(EMPTY_ROI_STATE);
  const [roster, setRoster] = useState({ loading: false, error: null, engineers: null });
  const [selectedFdeId, setSelectedFdeId] = useState(() => readStored(FDE_SELF_STORAGE_KEY));
  const [selectedOwnerId, setSelectedOwnerId] = useState(() =>
    readStored(OWNER_SELF_STORAGE_KEY),
  );

  const tabFromUrl = new URLSearchParams(location.search).get("tab");
  const activeTab = TABS.some((tab) => tab.key === tabFromUrl) ? tabFromUrl : "pilots";
  const isFdeTab = activeTab === "fde";
  const showsPilotData = activeTab === "pilots" || isFdeTab;
  const selectTab = (key) => history.replace({ search: `?tab=${key}` });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchPilots(env);
      if (!json?.success) {
        throw new Error(json?.reason || "Could not load pilots from HubSpot.");
      }
      setData(json);
    } catch (err) {
      setError(err.message || "Could not load pilots from HubSpot.");
      toast.error("Could not load pilots from HubSpot.");
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRoster = useCallback(async () => {
    setRoster({ loading: true, error: null, engineers: null });
    try {
      const json = await fetchFdeRoster(env);
      if (!json?.success) {
        throw new Error(json?.reason || "Could not load the FDE roster.");
      }
      setRoster({ loading: false, error: null, engineers: json.engineers || [] });
    } catch (err) {
      setRoster({
        loading: false,
        error: err.message || "Could not load the FDE roster.",
        engineers: null,
      });
    }
  }, [env]);

  // Only the FDE view needs the roster, so it loads when that tab opens.
  useEffect(() => {
    if (isFdeTab) {
      loadRoster();
    }
  }, [isFdeTab, loadRoster]);

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  const active = useMemo(() => data?.active || [], [data]);
  const past = useMemo(() => data?.past || [], [data]);
  const allPilots = useMemo(() => [...active, ...past], [active, past]);
  // A failed first load must not render as "no pilots running".
  const hasPilotData = Boolean(data) || (loading && !error);

  const selectFde = (fdeId) => {
    setSelectedFdeId(fdeId);
    store(FDE_SELF_STORAGE_KEY, fdeId);
  };

  const selectOwner = (ownerId) => {
    setSelectedOwnerId(ownerId);
    store(OWNER_SELF_STORAGE_KEY, ownerId);
  };

  return (
    <ClientOverviewProvider env={env}>
      <div className="pl-page">
        <ToastContainer position="top-right" autoClose={5000} />

        <header className="pl-hero">
          <div>
            <div className="pl-eyebrow">
              <FaFlask aria-hidden="true" /> Pilots
            </div>
            <h1 className="pl-title">Pilots &amp; clients</h1>
            <p className="pl-subtitle">
              Every running pilot from HubSpot, who owns and builds it, how the
              client is really using Addy, and what needs doing next — plus
              pilot-cost and client ROI calculators.
            </p>
            <div className="pl-chip-row">
              <span className="pl-chip">{formatNumber(active.length)} running</span>
              <span className="pl-chip">{formatNumber(past.length)} past pilots</span>
              {data?.generatedAt ? (
                <span className="pl-chip pl-chip--muted">
                  Live from HubSpot ·{" "}
                  {new Date(data.generatedAt).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              ) : null}
            </div>
          </div>
          <div className="pl-hero-actions">
            <EnvToggle env={env} onChange={setEnv} />
            <button
              type="button"
              className="pl-btn pl-btn--solid"
              onClick={load}
              disabled={loading}
            >
              <FaSync className={loading ? "fa-spin" : ""} aria-hidden="true" />
              Refresh
            </button>
          </div>
        </header>

        <nav className="pl-tabs" aria-label="Pilots sections">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={activeTab === tab.key ? "is-active" : ""}
              aria-current={activeTab === tab.key ? "page" : undefined}
              onClick={() => selectTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {error && showsPilotData ? (
          <div className="pl-banner pl-banner--error" role="alert">
            <strong>Could not load pilots from HubSpot.</strong>
            <pre>{error}</pre>
            <button type="button" className="pl-btn" onClick={load}>
              Try again
            </button>
          </div>
        ) : null}
        {showsPilotData && data && !data.pilotStageFound ? (
          <div className="pl-banner pl-banner--warn">
            No deal stage called “Pilot” was found in HubSpot, so only deals on a
            pilot step of the enterprise playbook are listed.
          </div>
        ) : null}
        {showsPilotData && data?.truncated ? (
          <div className="pl-banner pl-banner--warn">
            HubSpot returned more pilot deals than this page reads, so the oldest
            past pilots are missing.
          </div>
        ) : null}

        {activeTab === "pilots" && hasPilotData ? (
          <SalesView
            active={active}
            past={past}
            loading={loading}
            selectedOwnerId={selectedOwnerId}
            onSelectOwner={selectOwner}
          />
        ) : null}

        {isFdeTab && !data && loading ? (
          <div className="pl-empty">Loading pilots from HubSpot…</div>
        ) : null}
        {isFdeTab && data ? (
          <FdeView
            pilots={active}
            fdeOptions={data.fdeOptions || []}
            roster={roster}
            onRetryRoster={loadRoster}
            selectedFdeId={selectedFdeId}
            onSelectFde={selectFde}
          />
        ) : null}

        {activeTab === "cost" ? (
          <PilotCostCalculator
            pilots={active}
            pilotsLoading={loading}
            state={costState}
            onChange={setCostState}
          />
        ) : null}

        {activeTab === "roi" ? (
          <RoiCalculator pilots={allPilots} state={roiState} onChange={setRoiState} />
        ) : null}
      </div>
    </ClientOverviewProvider>
  );
}
